// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import './styles/main.css';

import { costCents, formatUsd } from './core/count';
import { bucketLabel, histogramMax } from './core/totals';
import { EXCLUDED, MODELS, TABLE_CHECKED, modelById } from './data/models';
import { OPENAI_WINDDOWN, PRICES, priceById, type PriceEntry } from './data/prices';
import { mountEventLog, log, setOpen as setLogOpen, isOpen as isLogOpen } from './eventlog';
import { bytesLabel, overLimitCsv, overLimitJsonl, reportHtml, statsCsv, type ReportInput } from './exports';
import { mountGlossary, term } from './glossary';
import { anyOpen, createModal, mountModalKeys, openModal } from './modal';
import { formatEta, poolSize, run, throughput, type RunHandle, type RunResult } from './pipeline';
import { ROLES } from './types';

const SETTINGS_KEY = 'epochcost:settings:v1';

interface Settings {
  modelId: string;
  epochs: number;
}

interface State {
  file: File | null;
  settings: Settings;
  limit: number | null;
  priceId: string;
  priceOverride: number | null;
  running: RunHandle | null;
  result: RunResult | null;
  tokenizerDescribe: string;
  status: string;
  error: string | null;
  progress: { bytesRead: number; bytesTotal: number; decompressedRead: number; rows: number } | null;
  startedAt: number;
}

function loadSettings(): Settings {
  const fallback: Settings = { modelId: 'gpt-4.1', epochs: 3 };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      modelId: MODELS.some((m) => m.id === parsed.modelId) ? (parsed.modelId as string) : fallback.modelId,
      epochs: Math.min(50, Math.max(1, Math.round(Number(parsed.epochs) || fallback.epochs))),
    };
  } catch {
    return fallback;
  }
}

const state: State = {
  file: null,
  settings: loadSettings(),
  limit: modelById(loadSettings().modelId).trainingLimit,
  priceId: 'together-lora-small',
  priceOverride: null,
  running: null,
  result: null,
  tokenizerDescribe: '',
  status: '',
  error: null,
  progress: null,
  startedAt: 0,
};

const app = document.getElementById('app') as HTMLElement;
const announce = document.getElementById('announce') as HTMLElement;

function say(message: string): void {
  // The live region lives OUTSIDE #app, because render() begins with
  // replaceChildren() and a region destroyed and rebuilt in the same frame as
  // the text announces nothing.
  announce.textContent = message;
}

function saveSettings(): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
  } catch {
    /* private mode: the preference simply does not persist */
  }
}

/* ── small builders ───────────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className) node.className = className;
  return node;
}

const num = (n: number): string => n.toLocaleString('en-US');

/**
 * Associate a label with a control that sits BESIDE it rather than inside it.
 *
 * Without this the accessible name is empty and a screen reader announces a row
 * of identical unnamed combo boxes. The visible rendering is the same either
 * way, which is why it is easy to ship broken.
 */
let uid = 0;
function label(labelEl: HTMLLabelElement, control: HTMLElement, extra?: string): void {
  const id = `ctl-${++uid}`;
  control.id = id;
  labelEl.htmlFor = id;
  if (extra) control.setAttribute('aria-label', `${labelEl.textContent} — ${extra}`);
}

/**
 * The footer node is MEMOISED.
 *
 * The hosted feedback widget appends its link into `.site-footer .footer-inner`
 * once, on DOMContentLoaded, and has no way to notice that the element it was
 * given has been replaced. render() calls app.replaceChildren() on every
 * interaction, so a footer rebuilt each time loses the widget's link after the
 * first click — present on first paint, gone forever after.
 */
let footerNode: HTMLElement | null = null;
function footer(): HTMLElement {
  if (footerNode) return footerNode;
  const foot = el('footer', undefined, 'site-footer');
  const inner = el('div', undefined, 'footer-inner');
  const built = el('span');
  built.innerHTML =
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
    '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>';
  inner.appendChild(built);
  foot.appendChild(inner);
  footerNode = foot;
  return foot;
}

/* ── focus preservation across a full re-render ───────────────────────────── */

/**
 * render() replaces every child of #app, so the element the user was operating
 * is detached on every keystroke and focus falls to <body>. Typing an epoch
 * count would lose the caret after each character. Controls carry a stable
 * `data-focus-key`, which is read before teardown and restored after.
 */
function captureFocus(): { key: string; start: number | null; end: number | null } | null {
  const active = document.activeElement as HTMLElement | null;
  const key = active?.dataset?.focusKey;
  if (!key) return null;
  const input = active as HTMLInputElement;
  const isText = input.tagName === 'INPUT' && input.type !== 'checkbox';
  return { key, start: isText ? input.selectionStart : null, end: isText ? input.selectionEnd : null };
}

function restoreFocus(saved: ReturnType<typeof captureFocus>): void {
  if (!saved) return;
  const node = app.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(saved.key)}"]`);
  if (!node) return;
  node.focus();
  if (saved.start !== null && node instanceof HTMLInputElement && node.type !== 'checkbox') {
    try {
      node.setSelectionRange(saved.start, saved.end);
    } catch {
      /* the input does not support selection */
    }
  }
}

/* ── header ───────────────────────────────────────────────────────────────── */

let modals: Record<string, HTMLElement> = {};

function header(): HTMLElement {
  const head = el('header', undefined, 'site-header');
  const brand = el('div', undefined, 'brand');

  const mark = el('span', undefined, 'brand-mark');
  mark.innerHTML =
    '<svg viewBox="0 0 64 64" width="26" height="26" aria-hidden="true">' +
    '<rect x="45" y="11" width="2" height="42" rx="1" fill="currentColor" opacity=".45"/>' +
    '<rect x="11" y="16" width="34" height="7" rx="3.5" fill="#f87171"/>' +
    '<rect x="49" y="16" width="6" height="7" rx="3.5" fill="#f87171" opacity=".42"/>' +
    '<rect x="11" y="26" width="30" height="7" rx="3.5" fill="#fbbf24"/>' +
    '<rect x="11" y="36" width="26" height="7" rx="3.5" fill="#fbbf24"/>' +
    '<rect x="11" y="46" width="17" height="7" rx="3.5" fill="#fbbf24" opacity=".72"/>' +
    '</svg>';

  const name = el('span', 'epochcost', 'brand-name');
  brand.append(mark, name);

  const nav = el('nav', undefined, 'site-nav');
  for (const [key, text] of [
    ['how', 'How it works'],
    ['privacy', 'Privacy'],
    ['about', 'About'],
  ] as const) {
    const button = el('button', text, 'ghost-btn');
    button.type = 'button';
    button.addEventListener('click', () => openModal(modals[key], button));
    nav.appendChild(button);
  }
  mountEventLog(nav);

  head.append(brand, nav);
  return head;
}

/* ── the drop zone ────────────────────────────────────────────────────────── */

function dropZone(): HTMLElement {
  const zone = el('div', undefined, 'dropzone');
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.setAttribute('aria-label', 'Choose a JSONL training file, or drop one here');

  const input = el('input');
  input.type = 'file';
  input.accept = '.jsonl,.json,.ndjson,.gz,.jsonl.gz,application/json,application/x-ndjson,application/gzip';
  input.hidden = true;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void start(file);
  });

  const title = el('p', 'Drop a fine-tuning .jsonl', 'dz-title');
  const hint = el('p', undefined, 'dz-hint');
  hint.append(
    document.createTextNode('One chat record per line. '),
    term('JSONL', 'jsonl'),
    document.createTextNode(' or .jsonl.gz. It is read here — nothing is uploaded.'),
  );

  const sample = el('button', 'Try a sample', 'pill-btn');
  sample.type = 'button';
  sample.addEventListener('click', (ev) => {
    // stopPropagation, or the click bubbles to the role=button zone and ALSO
    // opens the file picker on top of the sample loading.
    ev.stopPropagation();
    void loadSample();
  });
  sample.addEventListener('keydown', (ev) => {
    // Enter on the pill would otherwise bubble to the zone's keydown below and
    // open the file picker instead of loading the sample.
    if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation();
  });

  zone.append(title, hint, sample, input);

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      input.click();
    }
  });
  zone.addEventListener('dragover', (ev) => {
    ev.preventDefault();
    zone.classList.add('over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('over'));
  zone.addEventListener('drop', (ev) => {
    ev.preventDefault();
    zone.classList.remove('over');
    const file = ev.dataTransfer?.files?.[0];
    if (file) void start(file);
  });

  return zone;
}

/* ── controls ─────────────────────────────────────────────────────────────── */

function controls(): HTMLElement {
  const box = el('section', undefined, 'controls');

  /* model */
  const modelField = el('div', undefined, 'field');
  const modelLabel = el('label', 'Model');
  const modelSelect = el('select');
  modelSelect.dataset.focusKey = 'model';
  for (const model of MODELS) {
    const option = el('option', model.label);
    option.value = model.id;
    if (model.id === state.settings.modelId) option.selected = true;
    modelSelect.appendChild(option);
  }
  label(modelLabel, modelSelect);
  modelSelect.addEventListener('change', () => {
    state.settings.modelId = modelSelect.value;
    const model = modelById(modelSelect.value);
    state.limit = model.trainingLimit;
    saveSettings();
    state.result = null;
    log('info', `Model set to ${model.label}.`);
    render();
  });
  modelField.append(modelLabel, modelSelect);

  const model = modelById(state.settings.modelId);
  const modelNote = el('p', undefined, 'field-note');
  if (model.backend === 'hf') {
    modelNote.textContent =
      `Tokenizer downloaded from huggingface.co — about ${bytesLabel(model.downloadBytes ?? 0)}, once. ` +
      (model.note ?? '');
  } else {
    modelNote.textContent = 'Tokenizer is bundled — this model needs no network request at all.';
  }
  modelField.appendChild(modelNote);

  /* limit */
  const limitField = el('div', undefined, 'field');
  const limitLabel = el('label');
  limitLabel.append(term('Training example limit', 'training example limit'));
  const limitInput = el('input');
  limitInput.type = 'number';
  limitInput.min = '1';
  limitInput.step = '1';
  limitInput.value = state.limit === null ? '' : String(state.limit);
  limitInput.placeholder = 'set a limit';
  limitInput.dataset.focusKey = 'limit';
  label(limitLabel, limitInput, 'maximum tokens in one training record');
  limitInput.addEventListener('input', () => {
    const value = Number(limitInput.value);
    state.limit = Number.isFinite(value) && value > 0 ? Math.round(value) : null;
    state.result = null;
    render();
  });
  limitField.append(limitLabel, limitInput);

  const limitNote = el('p', undefined, 'field-note');
  if (model.trainingLimit !== null) {
    limitNote.textContent = `${num(model.trainingLimit)} by default — ${model.source}.`;
    if (model.contextWindow !== null && model.contextWindow !== model.trainingLimit) {
      limitNote.textContent += ` The context window is ${num(model.contextWindow)}, which is a different number and the wrong one to check against.`;
    }
  } else {
    limitNote.textContent = `${model.source}. epochcost will not invent one.`;
  }
  limitField.appendChild(limitNote);

  /* epochs */
  const epochField = el('div', undefined, 'field');
  const epochLabel = el('label');
  epochLabel.append(term('Epochs', 'epoch'));
  const epochInput = el('input');
  epochInput.type = 'number';
  epochInput.min = '1';
  epochInput.max = '50';
  epochInput.step = '1';
  epochInput.value = String(state.settings.epochs);
  epochInput.dataset.focusKey = 'epochs';
  label(epochLabel, epochInput, 'complete passes over the file');
  epochInput.addEventListener('input', () => {
    const value = Math.round(Number(epochInput.value));
    if (Number.isFinite(value) && value >= 1 && value <= 50) {
      state.settings.epochs = value;
      saveSettings();
      // Epochs are a MULTIPLIER on an already-computed sum, so this is free —
      // unlike the limit, which changes what each row contributes and needs
      // another pass.
      render();
    }
  });
  epochField.append(epochLabel, epochInput);

  /* price */
  const priceField = el('div', undefined, 'field');
  const priceLabel = el('label', 'Price');
  const priceSelect = el('select');
  priceSelect.dataset.focusKey = 'price';
  const groups = new Map<string, PriceEntry[]>();
  for (const price of PRICES) {
    const list = groups.get(price.provider) ?? [];
    list.push(price);
    groups.set(price.provider, list);
  }
  for (const [provider, list] of groups) {
    const group = document.createElement('optgroup');
    group.label = provider;
    for (const price of list) {
      const option = el('option', `${price.label} — $${price.perMillion}/1M`);
      option.value = price.id;
      if (price.id === state.priceId) option.selected = true;
      group.appendChild(option);
    }
    priceSelect.appendChild(group);
  }
  label(priceLabel, priceSelect);
  priceSelect.addEventListener('change', () => {
    state.priceId = priceSelect.value;
    state.priceOverride = null;
    render();
  });
  priceField.append(priceLabel, priceSelect);

  const price = priceById(state.priceId);
  const overrideRow = el('div', undefined, 'inline-field');
  const overrideLabel = el('label', '$ per 1M');
  const overrideInput = el('input');
  overrideInput.type = 'number';
  overrideInput.min = '0';
  overrideInput.step = '0.01';
  overrideInput.value = String(state.priceOverride ?? price.perMillion);
  overrideInput.dataset.focusKey = 'override';
  label(overrideLabel, overrideInput, 'correct this price if it has changed');
  overrideInput.addEventListener('input', () => {
    const value = Number(overrideInput.value);
    state.priceOverride = Number.isFinite(value) && value >= 0 ? value : null;
    render();
  });
  overrideRow.append(overrideLabel, overrideInput);
  priceField.appendChild(overrideRow);

  const priceNote = el('p', `Read from ${price.source} on ${TABLE_CHECKED}. Correct it above if it has moved.`, 'field-note');
  priceField.appendChild(priceNote);
  if (price.note) priceField.appendChild(el('p', price.note, 'field-note warn'));

  box.append(modelField, limitField, epochField, priceField);
  return box;
}

/* ── results ──────────────────────────────────────────────────────────────── */

function results(result: RunResult): HTMLElement {
  const section = el('section', undefined, 'results');
  const { totals } = result;
  const limit = state.limit;

  /* headline */
  const headline = el('div', undefined, 'headline');
  if (limit === null) {
    headline.appendChild(el('p', `${num(totals.rows)} records, ${num(totals.tokens)} tokens`, 'headline-figure'));
    headline.appendChild(
      el('p', 'Set a training example limit above to see which records would be cut.', 'headline-sub'),
    );
  } else if (totals.overLimit > 0) {
    const figure = el('p', undefined, 'headline-figure bad');
    figure.textContent = `${num(totals.overLimit)} of ${num(totals.rows)} records break the ${num(limit)}-token limit`;
    headline.appendChild(figure);
    const sub = el('p', undefined, 'headline-sub');
    sub.append(
      document.createTextNode(`${num(totals.truncatedTokens)} tokens sit past the limit. `),
      term('Truncation', 'truncation'),
      document.createTextNode(
        ' removes tokens from the end of a record, and the end of a chat record is the assistant’s reply.',
      ),
    );
    headline.appendChild(sub);
  } else {
    headline.appendChild(
      el('p', `No record breaks the ${num(limit)}-token limit`, 'headline-figure good'),
    );
    headline.appendChild(
      el('p', `${num(totals.rows)} records, longest ${num(totals.longest)} tokens.`, 'headline-sub'),
    );
  }
  section.appendChild(headline);

  /* cost */
  const price = state.priceOverride ?? priceById(state.priceId).perMillion;
  const billable = totals.cappedTokens * state.settings.epochs;
  const cents = costCents(billable, price);
  const costBox = el('div', undefined, 'cost-box');
  const costFigure = el('p', formatUsd(cents), 'cost-figure');
  costBox.appendChild(costFigure);
  const costSub = el('p', undefined, 'cost-sub');
  costSub.append(
    document.createTextNode(`${num(billable)} `),
    term('billable tokens', 'billable tokens'),
    document.createTextNode(
      ` for ${state.settings.epochs} epoch${state.settings.epochs === 1 ? '' : 's'} at $${price}/1M.`,
    ),
  );
  costBox.appendChild(costSub);
  if (limit !== null && totals.overLimit > 0) {
    costBox.appendChild(
      el(
        'p',
        `Each record is billed at the lesser of its own length and ${num(limit)}, because the trainer cuts it ` +
          `before it trains. Counting the raw lengths instead would say ${num(totals.tokens * state.settings.epochs)} tokens.`,
        'cost-note',
      ),
    );
  }
  const selected = priceById(state.priceId);
  if (selected.minimumUsd !== undefined && cents < selected.minimumUsd * 100) {
    costBox.appendChild(
      el('p', `Below ${selected.provider}’s $${selected.minimumUsd} minimum per job, so you would pay the minimum.`, 'cost-note'),
    );
  }
  if (selected.billsValidation) {
    costBox.appendChild(
      el(
        'p',
        `${selected.provider} also bills the tokens in your validation file, times the number of evaluations. ` +
          `That file is not counted here.`,
        'cost-note',
      ),
    );
  }
  section.appendChild(costBox);

  /* histogram */
  const histogram = el('div', undefined, 'histogram');
  histogram.appendChild(el('h2', 'Record lengths'));
  const max = histogramMax(totals.histogram);
  const table = el('table', undefined, 'hist-table');
  for (let i = 0; i < totals.histogram.counts.length; i++) {
    const count = totals.histogram.counts[i];
    if (count === 0) continue;
    const row = el('tr');
    row.appendChild(el('td', bucketLabel(totals.histogram.edges, i), 'hist-label'));
    const barCell = el('td', undefined, 'hist-bar-cell');
    const bar = el('span', undefined, 'hist-bar');
    bar.style.width = `${Math.max(2, Math.round((count / max) * 100))}%`;
    if (limit !== null && totals.histogram.edges[i] > limit) bar.classList.add('over');
    barCell.appendChild(bar);
    row.appendChild(barCell);
    row.appendChild(el('td', num(count), 'hist-count'));
    table.appendChild(row);
  }
  histogram.appendChild(table);
  section.appendChild(histogram);

  /* the work queue */
  if (limit !== null && result.overLimit.length > 0) {
    const queue = el('div', undefined, 'queue');
    queue.appendChild(el('h2', 'The records you have to fix'));
    const list = el('table', undefined, 'queue-table');
    const head = el('tr');
    for (const [text, cls] of [
      ['Record', ''],
      ['Tokens', 'n'],
      ['Cut', 'n'],
      ['Of the reply', 'n'],
    ] as const) {
      head.appendChild(el('th', text, cls));
    }
    list.appendChild(head);
    for (const row of result.overLimit.slice(0, 25)) {
      const tr = el('tr');
      tr.appendChild(el('td', `line ${num(row.ordinal)}`));
      tr.appendChild(el('td', num(row.tokens), 'n'));
      tr.appendChild(el('td', num(row.truncated), 'n bad'));
      tr.appendChild(el('td', row.assistantLost > 0 ? num(row.assistantLost) : '—', 'n'));
      list.appendChild(tr);
    }
    queue.appendChild(list);
    if (result.overLimit.length > 25) {
      queue.appendChild(el('p', `and ${num(result.overLimit.length - 25)} more — all of them are in the export.`, 'field-note'));
    }
    if (result.truncatedByCap > 0) {
      queue.appendChild(
        el(
          'p',
          `${num(result.truncatedByCap)} over-limit records were counted but are not in over-limit.jsonl, because ` +
            `the exported queue is bounded. Every number above includes them.`,
          'field-note warn',
        ),
      );
    }
    section.appendChild(queue);
  }

  /* problems */
  const problemRows = Object.entries(totals.problems).filter(([, n]) => n > 0);
  if (problemRows.length > 0) {
    const problems = el('div', undefined, 'problems');
    problems.appendChild(el('h2', 'Records epochcost could not count as chat'));
    const list = el('ul');
    const EXPLAIN: Record<string, string> = {
      'not-json': 'the line is not valid JSON',
      'no-messages': 'no "messages" array',
      'empty-messages': 'the "messages" array is empty',
      'message-not-object': 'a message is not an object',
      'content-not-string': 'some content is not text — an image part, for instance, whose cost depends on its size',
      'no-assistant': 'no assistant message, so there is nothing for the model to learn to say',
    };
    for (const [key, count] of problemRows) {
      list.appendChild(el('li', `${num(count)} — ${EXPLAIN[key] ?? key}`));
    }
    problems.appendChild(list);
    section.appendChild(problems);
  }

  /* totals */
  const detail = el('details', undefined, 'totals-detail');
  detail.appendChild(el('summary', 'All the numbers'));
  const totalsTable = el('table', undefined, 'totals-table');
  const rows: [string, string][] = [
    ['Records', num(totals.rows)],
    ['Messages', num(totals.messages)],
    ['Tokens, content plus framing', num(totals.tokens)],
    ['Tokens, content only', num(totals.contentTokens)],
    ['Longest record', `${num(totals.longest)} tokens`],
    ['Shortest record', `${num(totals.rows === 0 ? 0 : totals.shortest)} tokens`],
    ['Mean per record', `${num(totals.rows === 0 ? 0 : Math.round(totals.tokens / totals.rows))} tokens`],
    ['Read', bytesLabel(result.decompressedBytes)],
    ['Counted in', `${(result.elapsedMs / 1000).toFixed(1)}s across ${poolSize()} workers`],
  ];
  for (let i = 0; i < ROLES.length; i++) {
    if (totals.byRole[i] > 0) rows.push([`Content tokens, ${ROLES[i]}`, num(totals.byRole[i])]);
  }
  if (totals.blankLines > 0) rows.push(['Blank lines skipped', num(totals.blankLines)]);
  for (const [name, value] of rows) {
    const tr = el('tr');
    tr.appendChild(el('td', name));
    tr.appendChild(el('td', value, 'n'));
    totalsTable.appendChild(tr);
  }
  detail.appendChild(totalsTable);
  section.appendChild(detail);

  /* exports */
  const exports = el('div', undefined, 'exports');
  const input = (): ReportInput => ({
    fileName: state.file?.name ?? 'training.jsonl',
    fileBytes: state.file?.size ?? 0,
    model: modelById(state.settings.modelId),
    tokenizerDescribe: state.tokenizerDescribe,
    limit: state.limit,
    epochs: state.settings.epochs,
    price: priceById(state.priceId),
    priceOverride: state.priceOverride,
    totals,
    overLimit: result.overLimit,
    truncatedByCap: result.truncatedByCap,
  });

  const downloads: [string, string, () => Blob][] = [
    ['report.html', 'epochcost-report.html', () => reportHtml(input())],
    ['token-stats.csv', 'token-stats.csv', () => statsCsv(input())],
  ];
  if (limit !== null && result.overLimit.length > 0) {
    downloads.unshift(['over-limit.jsonl', 'over-limit.jsonl', () => overLimitJsonl(result.overLimit)]);
    downloads.push(['over-limit.csv', 'over-limit.csv', () => overLimitCsv(result.overLimit, limit)]);
  }

  for (const [text, filename, build] of downloads) {
    const button = el('button', text, 'download-btn');
    button.type = 'button';
    button.addEventListener('click', () => {
      // One download per user gesture. Chromium flips a tab to
      // "prompt before download" after the first programmatic one, so batching
      // them behind a single click means artefacts two and three silently never
      // arrive.
      const url = URL.createObjectURL(build());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 20_000);
      log('good', `Saved ${filename}.`);
    });
    exports.appendChild(button);
  }
  section.appendChild(exports);

  return section;
}

/* ── progress ─────────────────────────────────────────────────────────────── */

function progressView(): HTMLElement {
  const box = el('section', undefined, 'progress-box');
  const p = state.progress;
  const total = p?.bytesTotal || state.file?.size || 0;
  const read = p?.bytesRead ?? 0;
  const fraction = total > 0 ? Math.min(1, read / total) : 0;

  const bar = el('div', undefined, 'progress-track');
  const fill = el('div', undefined, 'progress-fill');
  fill.style.width = `${(fraction * 100).toFixed(1)}%`;
  bar.appendChild(fill);
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-valuemin', '0');
  bar.setAttribute('aria-valuemax', '100');
  bar.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
  box.appendChild(bar);

  const elapsed = performance.now() - state.startedAt;
  const rate = throughput(p?.decompressedRead ?? 0, elapsed);
  const line = el('p', undefined, 'progress-line');
  line.textContent =
    `${num(p?.rows ?? 0)} records · ${bytesLabel(read)} of ${bytesLabel(total)}` +
    (rate ? ` · ${(rate / 1048576).toFixed(1)} MB/s · ${formatEta(Math.max(0, total - read), rate)}` : ' · measuring…');
  box.appendChild(line);

  const cancel = el('button', 'Stop', 'ghost-btn');
  cancel.type = 'button';
  cancel.addEventListener('click', () => {
    state.running?.cancel();
    state.running = null;
    state.status = 'Stopped.';
    log('warn', 'Stopped by you. Nothing was kept.');
    render();
  });
  box.appendChild(cancel);

  return box;
}

/* ── running ──────────────────────────────────────────────────────────────── */

async function start(file: File): Promise<void> {
  if (state.running) state.running.cancel();
  state.file = file;
  state.result = null;
  state.error = null;
  state.progress = null;
  state.startedAt = performance.now();
  state.status = 'Counting…';

  const model = modelById(state.settings.modelId);
  log('info', `Reading ${file.name} — ${bytesLabel(file.size)}.`);
  if (model.backend === 'hf') {
    log('net', `Fetching ${model.tokenizer} tokenizer.json from huggingface.co (about ${bytesLabel(model.downloadBytes ?? 0)}).`);
  }
  say(`Counting ${file.name}.`);
  render();

  const handle = run(file, model, state.limit, {
    onProgress: (p) => {
      state.progress = p;
      // Repaint on a frame rather than on every message, or a fast file spends
      // its time in layout.
      scheduleRender();
    },
    onReady: (describe) => {
      state.tokenizerDescribe = describe;
      log('good', `Tokenizer ready: ${describe}.`);
    },
    onEvent: (message) => log('info', message),
  });
  state.running = handle;

  try {
    const result = await handle.result;
    state.result = result;
    state.running = null;
    state.status = '';
    const seconds = (result.elapsedMs / 1000).toFixed(1);
    log(
      'good',
      `Counted ${num(result.totals.rows)} records, ${num(result.totals.tokens)} tokens in ${seconds}s.`,
    );
    if (state.limit !== null) {
      log(
        result.totals.overLimit > 0 ? 'warn' : 'good',
        result.totals.overLimit > 0
          ? `${num(result.totals.overLimit)} records exceed the ${num(state.limit)}-token limit.`
          : `No record exceeds the ${num(state.limit)}-token limit.`,
      );
    }
    say(
      state.limit !== null && result.totals.overLimit > 0
        ? `${result.totals.overLimit} of ${result.totals.rows} records break the limit.`
        : `Counted ${result.totals.rows} records.`,
    );
  } catch (e) {
    state.running = null;
    state.status = '';
    state.error = e instanceof Error ? e.message : String(e);
    log('bad', state.error);
    say(`Could not finish: ${state.error}`);
  }
  render();
}

async function loadSample(): Promise<void> {
  try {
    log('net', 'Loading the bundled sample from this site.');
    const response = await fetch('/samples/finetune-train.jsonl.gz');
    if (!response.ok) throw new Error(`the sample could not be loaded (HTTP ${response.status})`);
    const blob = await response.blob();
    // Wrapped in a File and pushed through the SAME path as a real upload —
    // never a separate code path, or the demo proves nothing about the product.
    //
    // The reader sniffs gzip from the MAGIC BYTES rather than the extension, so
    // if a host serves this with Content-Encoding: gzip and the browser inflates
    // it transparently, the sample still loads — it simply takes the plain path.
    await start(new File([blob], 'finetune-train.jsonl.gz', { type: 'application/gzip' }));
  } catch (e) {
    state.error = e instanceof Error ? e.message : String(e);
    log('bad', state.error);
    render();
  }
}

/* ── render ───────────────────────────────────────────────────────────────── */

let renderQueued = false;
function scheduleRender(): void {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(() => {
    renderQueued = false;
    render();
  });
}

function render(): void {
  const saved = captureFocus();
  app.replaceChildren();

  app.appendChild(header());

  const main = el('main', undefined, 'main-content');

  const intro = el('section', undefined, 'intro');
  intro.appendChild(el('h1', 'What will this fine-tune cost, and which records break?'));
  const lede = el('p', undefined, 'lede');
  lede.append(
    document.createTextNode('Count a training file with the model’s real '),
    term('tokenizer', 'tokenizer'),
    document.createTextNode(', find the records longer than the '),
    term('training example limit', 'training example limit'),
    document.createTextNode(' — which is not the '),
    term('context window', 'context window'),
    document.createTextNode(' — and leave with the records you have to fix. It runs in this tab; the file is not uploaded.'),
  );
  intro.appendChild(lede);
  main.appendChild(intro);

  if (!state.file) main.appendChild(dropZone());
  else {
    const current = el('div', undefined, 'current-file');
    current.appendChild(el('span', `${state.file.name} — ${bytesLabel(state.file.size)}`, 'current-name'));
    const change = el('button', 'Use a different file', 'ghost-btn');
    change.type = 'button';
    change.addEventListener('click', () => {
      state.running?.cancel();
      state.running = null;
      state.file = null;
      state.result = null;
      state.error = null;
      render();
    });
    current.appendChild(change);
    main.appendChild(current);
  }

  main.appendChild(controls());

  if (state.error) {
    const error = el('div', undefined, 'error-box');
    error.appendChild(el('p', state.error));
    const retry = el('button', 'Try again', 'ghost-btn');
    retry.type = 'button';
    retry.addEventListener('click', () => {
      const file = state.file;
      state.error = null;
      if (file) void start(file);
      else render();
    });
    error.appendChild(retry);
    main.appendChild(error);
  } else if (state.running) {
    main.appendChild(progressView());
  } else if (state.result) {
    main.appendChild(results(state.result));
  } else if (state.file) {
    const rerun = el('button', 'Count it', 'primary-btn');
    rerun.type = 'button';
    rerun.addEventListener('click', () => {
      if (state.file) void start(state.file);
    });
    main.appendChild(rerun);
  }

  app.appendChild(main);
  app.appendChild(footer());
  restoreFocus(saved);
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

function boot(): void {
  modals = {
    how: createModal('modal-how', 'How it works', 'tpl-how'),
    privacy: createModal('modal-privacy', 'Privacy', 'tpl-privacy'),
    about: createModal('modal-about', 'About', 'tpl-about'),
  };

  // Fill the two lists that are generated rather than written, so the prose in
  // index.html and the data in TypeScript cannot drift apart.
  const excluded = document.getElementById('excluded-models');
  if (excluded) {
    for (const entry of EXCLUDED) {
      const li = document.createElement('li');
      const name = document.createElement('strong');
      name.textContent = `${entry.label}. `;
      li.append(name, document.createTextNode(entry.reason));
      excluded.appendChild(li);
    }
  }
  const checked = document.getElementById('table-checked');
  if (checked) checked.textContent = TABLE_CHECKED;
  const winddown = document.getElementById('openai-winddown');
  if (winddown) winddown.textContent = OPENAI_WINDDOWN;

  mountGlossary();
  mountModalKeys(() => {
    if (isLogOpen()) {
      setLogOpen(false);
      return true;
    }
    return false;
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === '?' && !anyOpen() && (ev.target as HTMLElement)?.tagName !== 'INPUT') {
      openModal(modals.how, null);
    }
  });

  render();
  log('info', `epochcost ready. ${poolSize()} counting workers.`);
}

boot();
